import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from "react-hook-form";
import { RadioButton } from 'primereact/radiobutton';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from "primereact/inputtext";
import { Button } from "../../../../../components/ui/Button";
import { COMMON_ASPECT_RATIOS } from '../../../../../config/layoutConstants';
import { useDispatch, useSelector } from "react-redux";
import { setSelectedArea } from "../../../../../redux/Slices/layoutSlice";
import { Checkbox } from "primereact/checkbox";

const AnimatedAreaControls = ({
    areas,
    currentAspectRatio,
    setAreas,
    setOverlappingAreas,
    setIsSaveDisabled,
    endPositionArea,
    setEndPositionArea,
    scrollableDivRef
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch()
    const { unit, selectedArea, screens_setting } = useSelector((state) => state.layout);
    const openDialog = useSelector((state) => state.global.openDialog);

    const { control, setValue, setError, clearErrors, formState: { errors }, getValues } = useForm();
    const [isDuplicatedNames, setIsDuplicatedNames] = useState(false);


    useEffect(() => {
        // Sync React Hook values with the area state
        areas.forEach((area, index) => {
            setValue(`areas[${index}].start_margin`, area.start_margin);
            setValue(`areas[${index}].top_margin`, area.top_margin);
            setValue(`areas[${index}].width`, area.width);
            setValue(`areas[${index}].height`, area.height);
            if (area.name) {
                setValue(`areas[${index}].name`, area.name || `Screen ${index + 1}`);
            }
        });
    }, [areas, setValue]);

    const handleSmartInputChange = (id, field, value, unit, currentAspectRatio) => {
        setAreas((prevAreas) =>
            prevAreas.map((area) => {
                if (area.id === id) {
                    let updatedValue = parseFloat(value) || 0;
                    let maxWidth = 100;
                    let maxHeight = 100;

                    if (unit?.value === "px" && currentAspectRatio) {
                        const aspectKey = `${currentAspectRatio.width}:${currentAspectRatio.height}`;
                        if (COMMON_ASPECT_RATIOS[aspectKey]) {
                            maxWidth = COMMON_ASPECT_RATIOS[aspectKey].width;
                            maxHeight = COMMON_ASPECT_RATIOS[aspectKey].height;
                        } else {
                            maxWidth = currentAspectRatio.width;
                            maxHeight = currentAspectRatio.height;
                        }
                    }

                    if (unit.value === "%") {
                        updatedValue = Math.min(100, Math.max(0, updatedValue));
                    } else if (unit.value === "px") {
                        updatedValue = Math.min(field === "width" || field === "start_margin" ? maxWidth : maxHeight, Math.max(0, updatedValue));
                    }

                    if (field === "start_margin") {
                        if (area.width === maxWidth) {
                            const newWidth = Math.max(0, area.start_margin + area.width - updatedValue);
                            return {
                                ...area,
                                start_margin: updatedValue,
                                width: unit.value === "%" ? Math.min(100, newWidth) : Math.min(maxWidth - updatedValue, newWidth),
                            };
                        } else {
                            return {
                                ...area,
                                start_margin: updatedValue,
                            };
                        }
                    } else if (field === "width") {
                        const newStartMargin = Math.min(
                            unit.value === "%" ? 100 - updatedValue : maxWidth - updatedValue,
                            area.start_margin
                        );
                        return {
                            ...area,
                            width: updatedValue,
                            start_margin: newStartMargin,
                        };
                    } else if (field === "top_margin") {
                        if (area.height === maxHeight) {
                            const newHeight = Math.max(0, area.top_margin + area.height - updatedValue);
                            return {
                                ...area,
                                top_margin: updatedValue,
                                height: unit.value === "%" ? Math.min(100, newHeight) : Math.min(maxHeight, newHeight),
                            };
                        } else {
                            return {
                                ...area,
                                top_margin: updatedValue,
                            };
                        }
                    } else if (field === "height") {
                        const newTopMargin = Math.min(
                            unit.value === "%" ? 100 - updatedValue : maxHeight - updatedValue,
                            area.top_margin
                        );
                        return {
                            ...area,
                            height: updatedValue,
                            top_margin: newTopMargin,
                        };
                    }
                }
                return area;
            })
        );
    };


    const handleDeleteArea = (id) => {
        if (id == endPositionArea) {
            setEndPositionArea()
        }
        const updatedAreas = areas.filter((area) => area.id !== id);
        const resetIdsAreas = updatedAreas.map((area, index) => ({
            ...area,
            id: typeof area.id === 'string' ? area.id : index + 1,
            order: index + 1,
        }));
        setAreas(resetIdsAreas);
    };
    const screenLabel = (area) => `Ad Slot ${area.order ?? area.id}`;
    const isDefaultScreenName = (name) =>
        /^Ad Slot\s*\d+$/i.test((name ?? '').trim());

    const renumberDefaultScreens = (list, endId = null) => {
        let n = 1;
        return list.map(a => {
            if (a.id === endId) return a; // keep "End Position" as-is
            if (a.name === "End Position" || isDefaultScreenName(a.name)) {
                return { ...a, name: `Ad Slot ${n++}` };
            }
            return a; // custom name: do not touch
        });
    };
    const markAsEndPosition = (checked, area) => {
        const newEndId = checked ? area.id : null;

        // First pass: set the clicked one, clear nothing else yet
        const provisional = areas.map(a => {
            if (a.id === newEndId) return { ...a, name: "End Position" };
            return a;
        });

        // Second pass: fix numbering for all default names,
        // and convert any old "End Position" back into "Screen N".
        const updated = renumberDefaultScreens(provisional, newEndId);

        setAreas(updated);
        setEndPositionArea(newEndId);
    };


    const checkDuplicateNames = () => {
        const names = areas.map((area) => area.name);
        const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
        if (duplicates.length > 0) {
            setError("duplicateNames", {
                type: "manual",
                message: `Duplicate names found: ${[...new Set(duplicates)].join(", ")}`,

            });
            return true

        } else {
            clearErrors("duplicateNames");
            return false
        }
    };

    const checkOverlap = () => {
        const overlappingAreas = [];
        for (let i = 0; i < areas.length; i++) {
            for (let j = i + 1; j < areas.length; j++) {
                const a = areas[i];
                const b = areas[j];
                const isOverlapping =
                    a.start_margin < b.start_margin + b.width &&
                    a.start_margin + a.width > b.start_margin &&
                    a.top_margin < b.top_margin + b.height &&
                    a.top_margin + a.height > b.top_margin;

                if (isOverlapping) {
                    overlappingAreas.push(i, j)
                }
            }
        }
        return Array.from(new Set(overlappingAreas));
    };


    useEffect(() => {
        const hasDuplicateNames = checkDuplicateNames();
        const overlappingAreas = checkOverlap();

        setIsDuplicatedNames(hasDuplicateNames);
        setOverlappingAreas(overlappingAreas);

        // Filter valid areas (exclude temporary ones with no ID)
        const validAreas = areas.filter(item => item?.id);
        const hasSameLength = screens_setting[0]?.related_screen?.length + 1 === validAreas.length;

        const screenIds = [
            ...(screens_setting ?? []).map(item => item.id),
            ...(screens_setting[0]?.related_screen ?? []).map(item => item.id)
        ].sort();
        const areaIds = validAreas.map(item => item.id).sort();
        const hasSameIds = screenIds.every((id, index) => id === areaIds[index]);
        const allScreens = [
            ...(screens_setting ?? []),
            ...(screens_setting[0]?.related_screen ?? [])
        ];

        let hasDifferentFieldValues = false;

        if (hasSameLength && hasSameIds) {
            // Compare width, height, and margins for each matching ID
            for (let id of screenIds) {

                const original = allScreens.find(item => item.id === id);
                const current = validAreas.find(item => item.id === id);
                let isDifferent

                if (original.settings.live.is_Animate_Screen_Enabled) {
                    isDifferent =
                        original.settings.live.Animated_Width !== current.width ||
                        // original.name !== current.name ||
                        original.settings.live.Animated_Height !== current.height ||
                        original.settings.live.Animated_start_margin !== current.start_margin ||
                        original.settings.live.order !== current.order ||
                        original.settings.live.Animated_top_margin !== current.top_margin;
                } else {

                    isDifferent =
                        original.width !== current.width ||
                        original.name !== current.name ||
                        original.height !== current.height ||
                        original.start_margin !== current.start_margin ||
                        original.settings.live.order !== current.order ||
                        original.top_margin !== current.top_margin;
                }


                if (isDifferent) {
                    hasDifferentFieldValues = true;
                    break;
                }
            }
        }

        // Enable save only if:
        // 1. No duplicates
        // 2. No overlaps
        // 3. Same length and IDs
        // 4. There are actual changes
        const hasAnyChanges =
            !hasDuplicateNames &&
            (
                !hasSameLength ||
                !hasSameIds ||
                hasDifferentFieldValues
            );


        setIsSaveDisabled(!hasAnyChanges);
    }, [areas]);


    const moveArea = (index, direction) => {
        const newAreas = [...areas];

        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newAreas.length) return;

        const temp = newAreas[index];
        newAreas[index] = newAreas[targetIndex];
        newAreas[targetIndex] = temp;

        const reorderedAreas = newAreas.map((area, i) => ({
            ...area,
            order: i + 1
        }));

        setAreas(reorderedAreas);
    };

    const compareByOrderThenStable = (a, b) => {
        const aHas = typeof a.order === "number";
        const bHas = typeof b.order === "number";
        if (aHas && bHas) return a.order - b.order;
        if (aHas) return -1;
        if (bHas) return 1;
        return 0;
    };

    const moveAreaById = (id, direction) => {
        // work on the same ordering used for rendering
        const sorted = [...areas].sort(compareByOrderThenStable);

        const idx = sorted.findIndex(a => a.id === id);
        if (idx === -1) return;

        const targetIdx = direction === "up" ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= sorted.length) return;

        // swap within the sorted list
        [sorted[idx], sorted[targetIdx]] = [sorted[targetIdx], sorted[idx]];

        // normalize order and write back
        const reordered = sorted.map((a, i) => ({ ...a, order: i + 1 }));
        setAreas(reordered);
    };

    // rgb(161 226 254 / 80%)
    return (
        <div className={` overflow-y-auto   min-h-0 max-h-[66%] `} ref={scrollableDivRef}>
            {errors.duplicateNames && (
                <div className="text-red-500 text-sm mt-2">{errors.duplicateNames.message}</div>
            )}
            {/* {areas.map((area, index) => (
                <div */}
            {areas
                .slice()
                .sort((a, b) => {
                    const aHasOrder = typeof a.order === 'number';
                    const bHasOrder = typeof b.order === 'number';

                    if (aHasOrder && bHasOrder) return a.order - b.order;
                    if (aHasOrder) return -1; // a comes before b
                    if (bHasOrder) return 1;  // b comes before a
                    return 0; // neither has order, keep original relative position
                })
                .map((area, viewIdx) => {
                    const idx = areas.findIndex(a => a.id === area.id);

                    return (
                        <div
                            id={`area_${viewIdx + 1}`}
                            key={area.id}
                            className={`items-center gap-3 mb-3 p-3 rounded-lg ${area.id === endPositionArea ? "bg-[#c3c3fc6b]" : "bg-gray-50"}  ${selectedArea?.id === area.id ? "border-3 border-[#8a8aed]" : "border border-gray-300"}`}
                        >
                            <div className=" flex gap-3">
                                <div className="flex flex-column justify-center items-center text-sm font-medium  ">

                                    <>
                                        <div className="cursor-pointer text-sky-500 hover:text-sky-700" onClick={() => {
                                            moveAreaById(area.id, 'up')
                                        }}><i class="fa-solid fa-sort-up"></i></div>
                                        <div className=""> {area.order} </div>
                                        <div className="cursor-pointer text-sky-500 hover:text-sky-700" onClick={() => moveAreaById(area.id, 'down')}><i class="fa-solid fa-sort-down"></i></div>

                                    </>


                                </div>
                                <div className="">
                                    <div className="flex items-center gap-3">

                                        {/* Editable Screen Name */}
                                        <div className="flex items-center  gap-1 w-50">
                                            <fieldset className="border border-gray-300 rounded flex flex-col items-center w-100  relative">
                                                <legend className={`text-xs px-1 absolute -top-2 left-2  ${area.id === endPositionArea ? "bg-transparent" : "bg-gray-50"}  w-auto`}>
                                                    {t('animated_layouts.slot_name')}
                                                </legend>
                                                <Controller
                                                    name={`areas[${idx}].name`}
                                                    control={control}
                                                    rules={{
                                                        required: t('animated_layouts.field_required'),
                                                        minLength: { value: 3, message: t('animated_layouts.name_too_short') },
                                                        maxLength: { value: 35, message: t('animated_layouts.name_too_long') }
                                                    }}
                                                    defaultValue={area.name || ""}
                                                    render={({ field, fieldState }) => (
                                                        <>
                                                            <InputText
                                                                {...field}
                                                                value={field.value}
                                                                disabled={area.id === endPositionArea}
                                                                onFocus={() => dispatch(setSelectedArea(area))}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (value.length > 35) return;
                                                                    field.onChange(value);
                                                                    setValue(`areas[${idx}].name`, value);
                                                                    setAreas((prevAreas) =>
                                                                        prevAreas.map((a, idx) =>
                                                                            a.id === area.id ? { ...a, name: value } : a
                                                                        )
                                                                    );
                                                                    checkDuplicateNames();
                                                                }}
                                                                placeholder={`Screen ${viewIdx + 1}`}
                                                            />
                                                            {fieldState.error && (
                                                                <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </fieldset>
                                        </div>

                                        <RadioButton
                                            checked={area.id === endPositionArea}
                                            onChange={(e) => markAsEndPosition(e.checked, area)}
                                        />
                                        <label className="text-sm w-[35%]">{t('animated_layouts.mark_end_position')}</label>
                                        {/* Delete Button */}
                                        <Button
                                            label=""
                                            icon="pi pi-trash"
                                            className=" bg-transparent text-red-500	 border-transparent"
                                            onClick={() => handleDeleteArea(area.id)}
                                        />
                                    </div>
                                    <div className="flex items-center  mt-3 gap-1 w-100">
                                        <div className="flex items-center gap-2 ">
                                            {/* Start Margin (X) */}
                                            <fieldset className="border border-gray-300 rounded flex flex-col items-center w-25 relative">
                                                <legend className={`text-xs px-1 absolute -top-2 left-2 ${area.id === endPositionArea ? "bg-transparent" : "bg-gray-50"} w-auto`}>
                                                    X {unit.value}
                                                </legend>
                                                <Controller
                                                    name={`areas[${idx}].start_margin`}
                                                    control={control}
                                                    defaultValue={area.start_margin}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            minFractionDigits={unit.value == "%" ? 1 : 0}
                                                            maxFractionDigits={unit.value == "%" ? 2 : 0}
                                                            onFocus={() => dispatch(setSelectedArea(area))}
                                                            value={field.value}
                                                            onChange={(e) => {
                                                                const value = e.value;
                                                                setValue(`areas[${idx}].start_margin`, value);
                                                                handleSmartInputChange(area.id, "start_margin", value, unit, currentAspectRatio);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </fieldset>
                                            {/* Top Margin (Y) */}
                                            <fieldset className="border border-gray-300 rounded flex flex-col items-center w-25 relative">
                                                <legend className="text-xs px-1 absolute -top-2 left-2 bg-gray-50 w-auto">
                                                    Y {unit.value}
                                                </legend>
                                                <Controller
                                                    name={`areas[${idx}].top_margin`}
                                                    control={control}
                                                    defaultValue={area.top_margin}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            minFractionDigits={unit.value == "%" ? 1 : 0}
                                                            maxFractionDigits={unit.value == "%" ? 2 : 0}
                                                            onFocus={() => dispatch(setSelectedArea(area))}
                                                            value={field.value}
                                                            onChange={(e) => {
                                                                const value = e.value;
                                                                setValue(`areas[${idx}].top_margin`, value);
                                                                handleSmartInputChange(area.id, "top_margin", value, unit, currentAspectRatio);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </fieldset>
                                            {/* Width */}
                                            <fieldset className="border border-gray-300 rounded flex flex-col items-center w-25 relative">
                                                <legend className="text-xs px-1 absolute -top-2 left-2 bg-gray-50 w-auto">
                                                    Width {unit.value}
                                                </legend>
                                                <Controller
                                                    name={`areas[${idx}].width`}
                                                    control={control}
                                                    defaultValue={area.width}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            minFractionDigits={unit.value == "%" ? 1 : 0}
                                                            maxFractionDigits={unit.value == "%" ? 2 : 0}
                                                            onFocus={() => dispatch(setSelectedArea(area))}
                                                            value={field.value}
                                                            onChange={(e) => {
                                                                const value = e.value;
                                                                setValue(`areas[${idx}].width`, value);
                                                                handleSmartInputChange(area.id, "width", value, unit, currentAspectRatio);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </fieldset>
                                            {/* Height */}
                                            <fieldset className="border border-gray-300 rounded flex flex-col items-center w-25 relative">
                                                <legend className="text-xs px-1 absolute -top-2 left-2 bg-gray-50 w-auto">
                                                    Height {unit.value}
                                                </legend>
                                                <Controller
                                                    name={`areas[${idx}].height`}
                                                    control={control}
                                                    defaultValue={area.height}
                                                    render={({ field }) => (
                                                        <InputNumber
                                                            {...field}
                                                            minFractionDigits={unit.value == "%" ? 1 : 0}
                                                            maxFractionDigits={unit.value == "%" ? 2 : 0}
                                                            value={field.value}
                                                            onFocus={() => dispatch(setSelectedArea(area))}
                                                            onChange={(e) => {
                                                                const value = e.value;
                                                                setValue(`areas[${idx}].height`, value);
                                                                handleSmartInputChange(area.id, "height", value, unit, currentAspectRatio);
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </fieldset>
                                        </div>

                                    </div>
                                </div>


                            </div>
                        </div>
                    )
                })}
        </div>
    );
};

export default AnimatedAreaControls;
